import dayjs from "dayjs"
import { useCallback, useEffect, useReducer, useRef } from "react"

import {
    ADDRESS,
    APPLICATION_ID_KW,
    APPLICATION_PAGE_SIZE,
    INSTALLER_DATE_FROM_KW,
    INSTALLER_DATE_TO_KW,
    INSTALLER_EMAIL,
    IS_REDACTED_ONLY,
    MAX_GEN_CAPACITY,
    MAX_MAX_DEMAND,
    MIN_GEN_CAPACITY,
    MIN_MAX_DEMAND,
    READY_FOR_CX,
    POSTCODE,
    STATUS,
    TECHNOLOGY,
    UPDATED_TO,
    UPDATED_SINCE,
    IS_UNREAD,
    IS_LOOPED_SUPPLY,
    IS_SUPPLY_ISSUES,
    INSTALLER_LAST_UPDATED,
    LAST_UPDATED,
} from "../../../../constants/generalConstants"
import { postClientProtected } from "../../clients/clients"
import { installerSingleApplicationsMapper } from "../../mappers/installerInterface/installerApplicationsMapper"
import {
    IApplicationListResponse,
    IListApplications,
} from "../../types/installerInterface/installerApplicationTypes"
import {
    INSTALLER_LIST_APPLICATIONS,
    INSTALLER_LIST_APPLICATIONS_MONTHLY,
} from "../../urls/hybrid"
import {
    getPreviousMonth,
    MAX_EMPTY_MONTHS,
} from "../dnoInterface/protected/useGetDnoApplicationsMonthly"

export interface InstallerMonthBucket {
    key: string
    label: string
    from: string
    to: string
    data: IListApplications
    nextPageKey: Record<string, string | number> | null
    isComplete: boolean
    isLoading: boolean
    isError: boolean
}

type State = {
    buckets: InstallerMonthBucket[]
    isAutoFilling: boolean
}

type Action =
    | { type: "ADD_MONTH"; bucket: InstallerMonthBucket }
    | { type: "FETCH_START"; monthKey: string }
    | {
          type: "FETCH_SUCCESS"
          monthKey: string
          data: IListApplications
          nextPageKey: Record<string, string | number> | null
      }
    | { type: "FETCH_ERROR"; monthKey: string }
    | { type: "SET_AUTO_FILLING"; value: boolean }
    | { type: "RESET"; initialBucket: InstallerMonthBucket }
    | { type: "RESET_FILTERED"; initialBucket: InstallerMonthBucket }
    | {
          type: "FETCH_SUCCESS_FILTERED"
          monthKey: string
          data: IListApplications
          nextPageKey: Record<string, string | number> | null
      }

export const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case "ADD_MONTH": {
            if (
                state.buckets.some((bucket) => bucket.key === action.bucket.key)
            ) {
                return state
            }
            return {
                ...state,
                buckets: [...state.buckets, action.bucket],
            }
        }
        case "FETCH_START":
            return {
                ...state,
                buckets: state.buckets.map((bucket) =>
                    bucket.key === action.monthKey
                        ? { ...bucket, isLoading: true, isError: false }
                        : bucket
                ),
            }
        case "FETCH_SUCCESS": {
            const hasMorePages = action.nextPageKey !== null
            return {
                ...state,
                buckets: state.buckets.map((bucket) =>
                    bucket.key === action.monthKey
                        ? {
                              ...bucket,
                              data: [...bucket.data, ...action.data],
                              nextPageKey: action.nextPageKey,
                              isComplete: !hasMorePages,
                              isLoading: false,
                          }
                        : bucket
                ),
            }
        }
        case "FETCH_ERROR":
            return {
                ...state,
                buckets: state.buckets.map((bucket) =>
                    bucket.key === action.monthKey
                        ? { ...bucket, isLoading: false, isError: true }
                        : bucket
                ),
            }
        case "SET_AUTO_FILLING":
            return { ...state, isAutoFilling: action.value }
        case "RESET":
            return {
                buckets: [action.initialBucket],
                isAutoFilling: true,
            }
        case "RESET_FILTERED":
            return {
                buckets: [action.initialBucket],
                isAutoFilling: false,
            }
        case "FETCH_SUCCESS_FILTERED": {
            const hasMorePages = action.nextPageKey !== null
            const grouped = new Map<string, IListApplications>()

            action.data.forEach((item) => {
                const monthKey = dayjs(item.createdDate).isValid()
                    ? dayjs(item.createdDate).format("YYYY-MM")
                    : action.monthKey

                const group = grouped.get(monthKey)
                if (group) {
                    group.push(item)
                } else {
                    grouped.set(monthKey, [item])
                }
            })

            let updatedBuckets = [...state.buckets]

            grouped.forEach((groupData, key) => {
                const existingIndex = updatedBuckets.findIndex(
                    (bucket) => bucket.key === key
                )
                if (existingIndex >= 0) {
                    const existing = updatedBuckets[existingIndex]
                    updatedBuckets[existingIndex] = {
                        ...existing,
                        data: [...existing.data, ...groupData],
                        nextPageKey: action.nextPageKey,
                        isComplete: !hasMorePages,
                        isLoading: false,
                    }
                } else {
                    const monthStart = dayjs(`${key}-01`)
                    updatedBuckets.push({
                        key,
                        label: monthStart.format("MMMM YYYY"),
                        from: monthStart.format("YYYY-MM-DD"),
                        to: monthStart.endOf("month").format("YYYY-MM-DD"),
                        data: groupData,
                        nextPageKey: action.nextPageKey,
                        isComplete: !hasMorePages,
                        isLoading: false,
                        isError: false,
                    })
                }
            })

            updatedBuckets = updatedBuckets.map((bucket) => {
                if (
                    bucket.key === action.monthKey &&
                    !grouped.has(action.monthKey)
                ) {
                    return {
                        ...bucket,
                        isComplete: true,
                        isLoading: false,
                    }
                }
                return bucket
            })

            updatedBuckets.sort((left, right) =>
                right.key > left.key ? 1 : -1
            )

            return {
                ...state,
                buckets: updatedBuckets,
            }
        }
        default:
            return state
    }
}

export const createInstallerMonthBucket = (
    monthStart: dayjs.Dayjs,
    isCurrentMonth: boolean
): InstallerMonthBucket => ({
    key: monthStart.format("YYYY-MM"),
    label: monthStart.format("MMMM YYYY"),
    from: monthStart.format("YYYY-MM-DD"),
    to: isCurrentMonth
        ? dayjs()
              .endOf("month")
              .hour(23)
              .minute(59)
              .second(59)
              .format("YYYY-MM-DDTHH:mm:ss")
        : monthStart
              .endOf("month")
              .hour(23)
              .minute(59)
              .second(59)
              .format("YYYY-MM-DDTHH:mm:ss"),
    data: [],
    nextPageKey: null,
    isComplete: false,
    isLoading: false,
    isError: false,
})

const INSTALLER_USER_FILTER_KEYS = [
    APPLICATION_ID_KW,
    INSTALLER_DATE_FROM_KW,
    INSTALLER_DATE_TO_KW,
    INSTALLER_LAST_UPDATED,
    UPDATED_SINCE,
    UPDATED_TO,
    STATUS,
    TECHNOLOGY,
    INSTALLER_EMAIL,
    POSTCODE,
    ADDRESS,
    MIN_GEN_CAPACITY,
    MAX_GEN_CAPACITY,
    MIN_MAX_DEMAND,
    MAX_MAX_DEMAND,
    IS_UNREAD,
    READY_FOR_CX,
    IS_LOOPED_SUPPLY,
    IS_SUPPLY_ISSUES,
    IS_REDACTED_ONLY,
    LAST_UPDATED,
]

type TGetApplicationsMonthly = {
    monthBuckets: InstallerMonthBucket[]
    allData: IListApplications
    isLoadingAny: boolean
    isAutoFilling: boolean
    isError: boolean
    isFinalMonth: boolean
    loadMonth: (monthKey: string) => void
    loadMore: () => void
    reset: () => void
}

export const useGetInstallerApplicationsMonthly = (
    searchParams: URLSearchParams,
    enabled = true
): TGetApplicationsMonthly => {
    const searchParamsLocal = new URLSearchParams(searchParams.toString())

    const searchParamsString = searchParamsLocal.toString()
    const hasUserFilters = INSTALLER_USER_FILTER_KEYS.some((key) =>
        searchParams.has(key)
    )
    const now = dayjs()
    const initialBucket = createInstallerMonthBucket(now.startOf("month"), true)

    const [state, dispatch] = useReducer(reducer, {
        buckets: [initialBucket],
        isAutoFilling: !hasUserFilters,
    })

    const fetchingRef = useRef(false)
    const abortRef = useRef(false)
    const consecutiveEmptyRef = useRef(0)
    const bucketsRef = useRef(state.buckets)
    bucketsRef.current = state.buckets

    let trailingEmpty = 0
    for (let index = state.buckets.length - 1; index >= 0; index -= 1) {
        const bucket = state.buckets[index]
        if (bucket.isComplete && bucket.data.length === 0) {
            trailingEmpty += 1
        } else {
            break
        }
    }
    consecutiveEmptyRef.current = trailingEmpty

    const fetchPage = useCallback(
        async (
            bucket: InstallerMonthBucket,
            cursor: Record<string, string | number> | null
        ) => {
            let url: string
            if (hasUserFilters) {
                url = INSTALLER_LIST_APPLICATIONS(
                    searchParamsString,
                    APPLICATION_PAGE_SIZE,
                    ""
                )
            } else {
                const monthlyParams = new URLSearchParams(searchParamsString)
                monthlyParams.delete(UPDATED_SINCE)
                monthlyParams.delete(UPDATED_TO)
                url = INSTALLER_LIST_APPLICATIONS_MONTHLY(
                    monthlyParams.toString(),
                    APPLICATION_PAGE_SIZE,
                    bucket.from,
                    bucket.to
                )
            }
            const res = await postClientProtected(url, cursor ?? null)
            return installerSingleApplicationsMapper.mapList(res)
        },
        [searchParamsString, hasUserFilters]
    )

    const loadMonth = useCallback(
        async (monthKey: string) => {
            const bucket = bucketsRef.current.find(
                (entry) => entry.key === monthKey
            )
            if (!bucket || bucket.isComplete || bucket.isLoading) return
            if (fetchingRef.current) return
            fetchingRef.current = true

            dispatch({ type: "FETCH_START", monthKey })

            try {
                const result: IApplicationListResponse = await fetchPage(
                    bucket,
                    bucket.data.length > 0 ? bucket.nextPageKey : null
                )
                if (abortRef.current) {
                    fetchingRef.current = false
                    return
                }

                dispatch({
                    type: hasUserFilters
                        ? "FETCH_SUCCESS_FILTERED"
                        : "FETCH_SUCCESS",
                    monthKey,
                    data: result.data,
                    nextPageKey: result.nextPageKey ?? null,
                })
            } catch {
                dispatch({ type: "FETCH_ERROR", monthKey })
            } finally {
                fetchingRef.current = false
            }
        },
        [fetchPage, hasUserFilters]
    )

    useEffect(() => {
        if (!enabled) return
        if (!state.isAutoFilling) return
        if (fetchingRef.current) return

        const totalRecords = state.buckets.reduce(
            (sum, bucket) => sum + bucket.data.length,
            0
        )

        if (totalRecords >= APPLICATION_PAGE_SIZE) {
            dispatch({ type: "SET_AUTO_FILLING", value: false })
            return
        }

        const lastBucket = state.buckets[state.buckets.length - 1]
        if (!lastBucket) return

        if (
            lastBucket.data.length === 0 &&
            !lastBucket.isComplete &&
            !lastBucket.isLoading &&
            !lastBucket.isError
        ) {
            loadMonth(lastBucket.key)
            return
        }

        if (lastBucket.isLoading) return

        if (lastBucket.isError) {
            dispatch({ type: "SET_AUTO_FILLING", value: false })
            return
        }

        if (lastBucket.isComplete) {
            if (consecutiveEmptyRef.current >= MAX_EMPTY_MONTHS) {
                dispatch({ type: "SET_AUTO_FILLING", value: false })
                return
            }

            if (totalRecords < APPLICATION_PAGE_SIZE) {
                const previousMonthStart = getPreviousMonth(lastBucket.key)
                dispatch({
                    type: "ADD_MONTH",
                    bucket: createInstallerMonthBucket(
                        previousMonthStart,
                        false
                    ),
                })
                return
            }

            dispatch({ type: "SET_AUTO_FILLING", value: false })
        }
    }, [enabled, state.buckets, state.isAutoFilling, loadMonth])

    useEffect(() => {
        if (!enabled) return
        if (state.isAutoFilling) return
        if (fetchingRef.current) return

        if (hasUserFilters) {
            const totalRecords = state.buckets.reduce(
                (sum, bucket) => sum + bucket.data.length,
                0
            )
            if (totalRecords >= APPLICATION_PAGE_SIZE) return
        }

        const unfetchedBucket = state.buckets.find(
            (bucket) =>
                bucket.data.length === 0 &&
                !bucket.isComplete &&
                !bucket.isLoading &&
                !bucket.isError
        )
        if (unfetchedBucket) {
            loadMonth(unfetchedBucket.key)
            return
        }

        if (hasUserFilters) return

        const lastBucket = state.buckets[state.buckets.length - 1]
        if (
            lastBucket &&
            lastBucket.isComplete &&
            lastBucket.data.length === 0 &&
            consecutiveEmptyRef.current < MAX_EMPTY_MONTHS
        ) {
            const previousMonthStart = getPreviousMonth(lastBucket.key)
            dispatch({
                type: "ADD_MONTH",
                bucket: createInstallerMonthBucket(previousMonthStart, false),
            })
        }
    }, [enabled, state.buckets, state.isAutoFilling, loadMonth, hasUserFilters])

    const prevSearchParamsRef = useRef(searchParamsString)
    useEffect(() => {
        if (prevSearchParamsRef.current !== searchParamsString) {
            prevSearchParamsRef.current = searchParamsString
            abortRef.current = true
            fetchingRef.current = false
            consecutiveEmptyRef.current = 0

            const resetBucket = createInstallerMonthBucket(
                dayjs().startOf("month"),
                true
            )
            dispatch({
                type: hasUserFilters ? "RESET_FILTERED" : "RESET",
                initialBucket: resetBucket,
            })

            setTimeout(() => {
                abortRef.current = false
            }, 0)
        }
    }, [searchParamsString, hasUserFilters])

    const visibleBuckets = state.buckets.filter(
        (bucket) => bucket.data.length > 0
    )
    const allData = state.buckets.reduce(
        (acc: IListApplications, bucket) => [...acc, ...bucket.data],
        []
    )
    const isLoadingAny = state.buckets.some((bucket) => bucket.isLoading)
    const isError = state.buckets.some((bucket) => bucket.isError)
    const hasMorePages = state.buckets.some(
        (bucket) => !bucket.isComplete && !bucket.isLoading
    )
    const isFinalMonth = hasUserFilters
        ? !hasMorePages
        : consecutiveEmptyRef.current >= MAX_EMPTY_MONTHS

    const loadMore = useCallback(() => {
        const buckets = bucketsRef.current
        const incompleteBucket = buckets.find(
            (bucket) => !bucket.isComplete && !bucket.isLoading
        )
        if (incompleteBucket) {
            loadMonth(incompleteBucket.key)
            return
        }

        if (hasUserFilters) return

        const lastBucket = buckets[buckets.length - 1]
        if (lastBucket) {
            const previousMonthStart = getPreviousMonth(lastBucket.key)
            dispatch({
                type: "ADD_MONTH",
                bucket: createInstallerMonthBucket(previousMonthStart, false),
            })
        }
    }, [loadMonth, hasUserFilters])

    const reset = useCallback(() => {
        abortRef.current = true
        fetchingRef.current = false
        consecutiveEmptyRef.current = 0
        dispatch({
            type: "RESET",
            initialBucket: createInstallerMonthBucket(
                dayjs().startOf("month"),
                true
            ),
        })
        setTimeout(() => {
            abortRef.current = false
        }, 0)
    }, [])

    return {
        monthBuckets: visibleBuckets,
        allData,
        isLoadingAny,
        isAutoFilling: state.isAutoFilling,
        isError,
        isFinalMonth,
        loadMonth,
        loadMore,
        reset,
    }
}
